<template>
  <v-container fluid class="theme_background borderRadius">
    <v-row>
      <v-col cols="12" class="px-0">
        <p class="ml-2 right_panel_title">
          <v-avatar color="rgb(var(--v-theme-dark-color))" size="small" class="pa-0 mr-2">
            <v-icon color="rgb(var(--v-theme-lighten))" large>mdi-pine-tree</v-icon>
          </v-avatar>
          {{ $t('domain.fields.fuelType') }}
        </p>
      </v-col>
      <v-row class="mt-4">
        <v-col cols="12" class="pt-0">
          <apexchart v-if="seriesComputed" :key="theme" type="donut" :options="chartOptions" :series="seriesComputed" height="350" />
          <v-row v-else class="justify-center">
            <v-col cols="10" class="p-0">
              <div class="d-flex justify-center align-center opacity87">
                <v-icon color="primary">mdi-information-outline</v-icon>
                <span class="ml-2 font-weight-bold">{{ $t('common.concepts.noData') }}</span>
              </div>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </v-row>
  </v-container>
</template>

<script lang="ts" setup>
// Frameworks import
import type { PropType } from 'vue'
import { computed } from 'vue'

import { storeToRefs } from 'pinia'

import { useI18n } from 'vue-i18n'

//Types import
import settingsStore from '@/stores/settingsStore.ts'
import type { ValueBufferType, TypedHist } from '@/types/api/Calculations.ts'

// Settings
const settings = settingsStore()
const { theme } = storeToRefs(settings)

const props = defineProps({
  data: {
    type: Object as PropType<ValueBufferType | undefined>,
    required: true
  }
})

const { t } = useI18n()

const seriesComputed = computed(() => {
  return props.data?.typedHist?.map((x: TypedHist) => x.percentage)
})

const colorsComputed = computed(() => {
  return props.data?.typedHist?.map((x: TypedHist) => x.calculationType.color)
})

const namesComputed = computed(() => {
  return props.data?.typedHist?.map((x: TypedHist) => t(`domain.enums.fuelType.${x.calculationType.name}`))
})

const chartOptions = computed(() => {
  const strokeColors: Record<string, string> = {
    light: '#cccccc',
    dark: '#2f2f2f'
  }
  return {
    labels: namesComputed.value,
    colors: colorsComputed.value,
    legend: {
      position: 'bottom',
      fontSize: '12px',
      labels: {
        colors: 'rgb(var(--v-theme-lighten))'
      },
      offsetY: 0,
      horizontalAlign: 'left',
      formatter: function (seriesName: string, opts: { w: { globals: { series: number[] } }; seriesIndex: number }) {
        return [seriesName, `(${opts.w.globals.series[opts.seriesIndex]} %)`]
      }
    },
    plotOptions: {
      pie: {
        donut: {
          size: '80%'
        }
      }
    },
    dataLabels: {
      enabled: false
    },
    tooltip: {
      enabled: true,
      fillSeriesColor: false
    },
    stroke: {
      show: true,
      colors: [strokeColors[theme.value]],
      width: 2.5
    },
    fill: {
      pattern: {
        width: 60,
        height: 6,
        strokeWidth: 4
      }
    }
  }
})
</script>

<style scoped lang="scss"></style>
